import { IChatRooms } from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useQuery } from '@tanstack/react-query';

type GetChatRoomsProps = {
  businessEntityId?: number;
  companyId?: number;
  excludeEmptyChatRooms?: boolean;
  agentFirmId?: string;
  /** Rooms across the clients the caller can see in this affiliate firm. Not combinable with agentFirmId (400). */
  affiliateFirmId?: string;
  userId?: string;
  excludeCompletedServiceChatRooms?: boolean;
  /** When false the query is skipped entirely (no network request). */
  enabled?: boolean;
};

const getChatRooms = async (props: GetChatRoomsProps) => {
  // add query params
  const queryParams = new URLSearchParams();
  if (props.businessEntityId)
    queryParams.append('corporationId', props.businessEntityId.toString());
  if (props.companyId)
    queryParams.append('companyId', props.companyId.toString());

  if (props.excludeEmptyChatRooms)
    queryParams.append(
      'excludeEmptyChatRooms',
      props.excludeEmptyChatRooms ? 'true' : 'false'
    );

  if (props.excludeCompletedServiceChatRooms)
    queryParams.append(
      'excludeCompletedServiceChatRooms',
      props.excludeCompletedServiceChatRooms ? 'true' : 'false'
    );

  if (props.agentFirmId) queryParams.append('agentFirmId', props.agentFirmId);

  if (props.affiliateFirmId)
    queryParams.append('affiliateFirmId', props.affiliateFirmId);

  if (props.userId) queryParams.append('userId', props.userId);

  const response = await fetcher(`chatRoom?${queryParams.toString()}`, {
    method: 'GET',
  });

  if (!response.ok) throw new Error('Failed to fetch business entity');

  return (await response.json()) as IChatRooms;
};

export const chatRoomKeys = {
  FETCH_CHAT_ROOMS: 'FETCH_CHAT_ROOMS',
};

const useChatRooms = ({ enabled = true, ...props }: GetChatRoomsProps) => {
  return useQuery({
    queryKey: [chatRoomKeys.FETCH_CHAT_ROOMS, props],
    queryFn: () => getChatRooms(props),
    enabled,
    // Chat unread counts must always be fresh — override the global 5-min staleTime.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export { useChatRooms };
